import { ListRecentFailuresResponse } from '@/api/generatedApi';
import { useInfiniteQuery } from '@/hooks/api/useInfiniteQuery';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';

export const RECENT_FAILURES_QUERY_KEY = 'recentFailures';
export const RECENT_FAILURES_PAGE_SIZE = 10;

export type FailureKind = 'asset' | 'report' | 'part-finder-report';

export function useGetRecentFailures(kind?: FailureKind) {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  // The filter has to reach the server: the list is paginated, so filtering the rows already
  // fetched would hide matches sitting on later pages.
  const kinds = kind ?? '';

  const query = useInfiniteQuery<
    { tenantId: string; kinds: string; page?: number; perPage?: number },
    ListRecentFailuresResponse,
    'failures'
  >({
    queryKey: [RECENT_FAILURES_QUERY_KEY, { tenantId, kinds }],
    queryFunction: ({ tenantId: forTenant, kinds: forKinds, page, perPage }) =>
      api.failures.listRecentFailures({
        tenantId: forTenant,
        page: page ?? 1,
        perPage: perPage ?? RECENT_FAILURES_PAGE_SIZE,
        ...(forKinds && { kinds: forKinds }),
      }),
    queryParams: { tenantId, kinds },
    // Drives the hook's pagination model; without it the shared 50-row grid default applies
    // and a tenant's whole failure list arrives in one page.
    // A kind's list unmounts while its count is zero, so it must refetch on remount once live
    // refresh has marked it stale, or a failure that comes back shows an empty list.
    options: {
      enabled: !!tenantId,
      perPage: RECENT_FAILURES_PAGE_SIZE,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
    dataKey: 'failures',
  });

  // Tenant-wide totals, not per page, so read them off the most recent page rather than
  // accumulating them like the rows.
  const pages = query.rawResult.data?.pages;
  const latestPage = pages?.at(-1)?.data;
  const countsByKind = latestPage?.countsByKind;
  const mostRecentByKind = latestPage?.mostRecentByKind;

  return { ...query, countsByKind, mostRecentByKind };
}
